import { useEffect, useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useAtomValue, useSetAtom } from 'jotai';

import type { ImageAnalyses } from '@/@types/latestImagesV2';
import {
  formatBoneLevel,
  generateFmxTitle,
  getUniqueTeethForSelectedTxs,
} from '@/modules/claims/cleanClaims/helpers';
import { getFindings } from '@/modules/claims/cleanClaims/images/helpers';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';
import {
  claimAttachmentFmxImagesAtom,
  claimAttachmentFmxTableDataAtom,
  excludeFmxFindingsWhenPeriochartPresentAtom,
  selectedTreatmentsAtom,
} from '@/modules/claims/freeClaims/state';

import { FmxLayout } from './FmxLayout';
import { SrpTreatmentsTable } from './SrpTreatmentsTable';

type QuadLevelImagesGroupProps = {
  filteredImages: ImageAnalyses[];
  fmxGrid: (ImageAnalyses | null)[];
  claimSummary?: GetClaimSummaryResponse;
  showAnnotations?: boolean;
  showTable?: boolean;
  showOriginal?: boolean;
};

export const QuadLevelImagesGroup = ({
  claimSummary,
  fmxGrid,
  filteredImages,
  showAnnotations = false,
  showTable = false,
  showOriginal = false,
}: QuadLevelImagesGroupProps) => {
  const selectedTreatmentIds = useAtomValue(selectedTreatmentsAtom);
  const setFmxImages = useSetAtom(claimAttachmentFmxImagesAtom);
  const setFmxTableData = useSetAtom(claimAttachmentFmxTableDataAtom);
  const excludeFmxFindingsWhenPeriochartPresent = useAtomValue(
    excludeFmxFindingsWhenPeriochartPresentAtom,
  );

  // hooks
  const { t } = useLingui();

  // Compute the unique teeth findings for the FMX grid and selected treatments only once.
  // This array is used for both the FMX images atom and the SRP table.
  const uniqueTeethWithFindings = useMemo(
    () =>
      getUniqueTeethForSelectedTxs(
        claimSummary?.fmx ?? [],
        selectedTreatmentIds?.map((t) => Number(t)) ?? [],
        claimSummary?.treatments || [],
        filteredImages,
      ),
    [
      claimSummary?.fmx,
      selectedTreatmentIds,
      claimSummary?.treatments,
      filteredImages,
    ],
  );

  const hasPeriochart =
    claimSummary?.perioChart?.teeth && claimSummary.perioChart.teeth.length > 0;

  const shouldShowTable =
    showTable &&
    uniqueTeethWithFindings.length > 0 &&
    !(excludeFmxFindingsWhenPeriochartPresent && hasPeriochart);

  const fmxTitle = generateFmxTitle(
    claimSummary?.treatments,
    selectedTreatmentIds,
  );

  // Build the FMX images array for the atom whenever the FMX grid or findings change.
  // Only store data needed for PDF generation: imageId and annotations
  useEffect(() => {
    const fmxImages = fmxGrid.map((img) => {
      if (!img) return null;
      const original =
        filteredImages.find((i) => i.imageId === img.imageId) || img;
      const options = {
        enabledFindings: ['radiographic_boneloss', 'calculus'],
        showIncipientCariesRed: false,
        cariesBin: undefined,
      };
      const annotations = getFindings(original, options);
      return {
        imageId: img.imageId,
        annotations,
      };
    });
    setFmxImages(fmxImages);

    // Set fmxTableData in state - only data needed for PDF generation
    const fmxTableData = uniqueTeethWithFindings.map((tooth) => ({
      tooth: tooth.toothId,
      txCodes: tooth.txCodes,
      calculus: tooth.potentialCalculus ?? false,
      rblMesial: formatBoneLevel(tooth.mesial, tooth.measurementType) ?? null,
      rblDistal: formatBoneLevel(tooth.distal, tooth.measurementType) ?? null,
    }));
    setFmxTableData(fmxTableData);
  }, [
    uniqueTeethWithFindings,
    fmxGrid,
    filteredImages,
    setFmxImages,
    setFmxTableData,
  ]);

  return (
    <div
      className={`vai-rounded-lg vai-overflow-hidden vai-border vai-border-slate-200 vai-w-full ${
        !showAnnotations ? 'vai-border-b-0 vai-rounded-b-none' : ''
      } ${!showOriginal ? 'vai-border-t-0 vai-rounded-t-none' : ''}`}
    >
      {showOriginal && (
        <h3 className="vai-text-left vai-w-full vai-border-b vai-border-slate-200 vai-bg-slate-100 vai-p-2 vai-text-sm vai-font-semibold">
          {fmxTitle}
        </h3>
      )}
      <div className="vai-w-full vai-font-normal vai-text-slate-600 vai-p-4 vai-flex vai-flex-col vai-gap-8 vai-min-w-fit">
        {showOriginal && (
          <FmxLayout fmxGrid={fmxGrid} header={t`ORIGINAL`} annotated={false} />
        )}
        {showAnnotations && (
          <FmxLayout fmxGrid={fmxGrid} header={t`ANNOTATED`} annotated={true} />
        )}
        {shouldShowTable && (
          <SrpTreatmentsTable uniqueTeeth={uniqueTeethWithFindings} />
        )}
      </div>
    </div>
  );
};
